import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { EASE, Reveal, scrollToHash } from "@/components/Reveal";
import { HERO_IMG } from "@/components/Hero";

const PLANE_IMG =
  "https://images.unsplash.com/photo-1734455823834-c89ebb125aa3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHBsYW5lJTIwbmlnaHR8ZW58MHx8fHwxNzg2NTU4NjExfDA&ixlib=rb-4.1.0&q=85&w=1400";
const WAREHOUSE_IMG =
  "https://images.unsplash.com/photo-1644079446600-219068676743?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBzaGVsdmVzJTIwZGFyayUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4NjU1ODYxMXww&ixlib=rb-4.1.0&q=85&w=1400";
const LAND_TRANSPORT_IMG = "/assets/land-transport.png";
const CUSTOMS_CLEARANCE_IMG = "/assets/customs-clearance.png";

const SERVICES = [
  {
    id: "01",
    slug: "air-freight",
    title: "Air Freight",
    desc: "When hours decide the deal, our air division puts your cargo on the next wheels-up - express, consolidated or full charter, with dangerous-goods certified handling at both ends.",
    img: PLANE_IMG,
    alt: "Cargo aircraft on the tarmac at night",
    tags: ["48h express", "Full charter", "DG certified"],
  },
  {
    id: "02",
    slug: "ocean-freight",
    title: "Ocean Freight",
    desc: "FCL and LCL on every major trade lane. Fixed weekly sailings, reefer capability and port-to-door management through alliances with 40+ carriers.",
    img: HERO_IMG,
    alt: "Container ship crossing open ocean",
    tags: ["FCL / LCL", "Reefer", "Port-to-door"],
  },
  {
    id: "03",
    slug: "land-transport",
    title: "Land Transport",
    desc: "FTL, LTL and final-mile across continents - bonded corridors, cross-dock networks and drivers who know the border formalities by heart.",
    img: LAND_TRANSPORT_IMG,
    alt: "Portwave truck moving cargo near a port",
    tags: ["FTL / LTL", "Bonded corridors", "Final mile"],
  },
  {
    id: "04",
    slug: "customs-clearance",
    title: "Customs Clearance",
    desc: "In-house brokers in 30 markets. Duties engineered, documentation pre-cleared and compliance settled before your cargo ever reaches the border.",
    img: CUSTOMS_CLEARANCE_IMG,
    alt: "Customs clearance documentation and inspection",
    tags: ["30 markets", "Pre-clearance", "Duty engineering"],
  },
  {
    id: "05",
    slug: "warehousing",
    title: "Warehousing",
    desc: "2.4 million sqm of bonded and temperature-controlled storage. Cross-docking, fulfilment and inventory visibility down to the last pallet.",
    img: WAREHOUSE_IMG,
    alt: "High-bay warehouse aisles under low light",
    tags: ["Bonded", "Temp-controlled", "Fulfilment"],
  },
];

function ServiceRow({ service, open, onToggle }) {
  return (
    <div className="border-b border-white/10">
      <button
        data-testid={`service-row-${service.slug}`}
        onClick={onToggle}
        aria-expanded={open}
        className="group grid w-full grid-cols-12 items-center gap-4 py-7 text-left lg:py-9"
      >
        <span className="col-span-3 text-xs font-semibold tracking-[0.25em] text-white/40 md:col-span-2">
          / {service.id}
        </span>
        <span
          className={`col-span-7 font-display text-3xl font-medium uppercase tracking-tight transition-[transform,color] duration-500 group-hover:translate-x-2 md:col-span-8 md:text-5xl ${
            open ? "text-[#FF3B30]" : "text-white"
          }`}
        >
          {service.title}
        </span>
        <span className="col-span-2 flex justify-end">
          <span
            className={`flex h-10 w-10 items-center justify-center border transition-[transform,background-color,border-color] duration-500 ${
              open
                ? "rotate-45 border-[#FF3B30] bg-[#FF3B30]"
                : "border-white/20 group-hover:border-white/50"
            }`}
          >
            <Plus className="h-4 w-4" />
          </span>
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="grid gap-10 pb-12 md:grid-cols-2 md:items-center">
              <div className="clip-corner group/img relative overflow-hidden">
                <img
                  src={service.img}
                  alt={service.alt}
                  loading="lazy"
                  className="aspect-[16/10] w-full object-cover grayscale transition-[transform,filter] duration-700 group-hover/img:scale-105 group-hover/img:grayscale-0"
                />
                <span className="absolute left-0 top-0 h-1 w-16 bg-[#FF3B30]" />
              </div>
              <div>
                <p className="max-w-lg text-base leading-relaxed text-white/70 md:text-lg">
                  {service.desc}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {service.tags.map((tag) => (
                    <span
                      key={tag}
                      className="border border-white/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-white/60"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <a
                  href="#contact"
                  data-testid={`service-cta-${service.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToHash("#contact");
                  }}
                  className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] transition-colors duration-300 hover:text-white"
                >
                  Request this service
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Services() {
  const [openSlug, setOpenSlug] = useState("air-freight");

  return (
    <section
      id="services"
      data-testid="services-section"
      className="scroll-mt-20 px-6 py-28 lg:px-12 lg:py-40"
    >
      <Reveal>
        <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-white/50">
          <span className="h-px w-10 bg-[#FF3B30]" />
          What we do
        </p>
        <h2 className="mt-6 max-w-4xl font-display text-4xl font-semibold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-7xl">
          Five disciplines.
          <br />
          <span className="text-white/40">One unbroken chain.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.15} className="mt-16 lg:mt-20">
        <div className="border-t border-white/10">
          {SERVICES.map((service) => (
            <ServiceRow
              key={service.slug}
              service={service}
              open={openSlug === service.slug}
              onToggle={() =>
                setOpenSlug((s) => (s === service.slug ? null : service.slug))
              }
            />
          ))}
        </div>
      </Reveal>
    </section>
  );
}
